import { Dispatch, SetStateAction } from "react";
import { Badge, Icon, Text, Thumbnail } from "@shopify/polaris";
import { EditIcon, ProductIcon } from "@shopify/polaris-icons";
import styles from "./Intents.module.css";
import { endPoints, postData } from "../../utilities/networkCalls";
import { ChangeEvents } from "../../apps/Bundles/Types";

interface ProductIntentProps {
    bundleConfig: any;
    bundle_as_product: any;
    product_bundle_id: string;
    dispatchBundleConfig: Dispatch<any>;
    setConfig: Dispatch<SetStateAction<any>>;
    shakeSaveBar: () => void;
    globalDirty: boolean;
    setSavingBundle: Dispatch<SetStateAction<boolean>>;
    product_title: string;
}

// const fetchProductInfo = async (sft: string, productId: string) => {
//     const query = `
//     query getProduct($id: ID!) {
//       node(id: $id) {
//         ... on Product {
//           id
//           title
//           handle
//           images(first: 50) {
//             edges {
//               node {
//                 id
//                 url
//                 altText
//               }
//             }
//           }
//         }
//       }
//     }
//   `;

//     const variables = { id: productId };

//     const headers = new Headers({
//         "Content-Type": "application/json",
//         "X-Shopify-Storefront-Access-Token": sft,
//     });

//     try {
//         const userShopname = shopify.config?.shop;

//         const response = await fetch(`https://${userShopname}/api/2025-10/graphql.json`, {
//             method: "POST",
//             headers,
//             body: JSON.stringify({ query, variables }),
//         });

//         const result = await response.json();

//         if (result?.errors?.length) {
//             console.error("Fetch product storefront api error:", result.errors);
//             return null;
//         }

//         const product = result?.data?.node;
//         if (!product) return null;

//         return {
//             id: product.id,
//             title: product.title,
//             handle: product.handle,
//             images:
//                 product.images?.edges?.map((edge: any) => ({
//                     originalSource: edge.node.url,
//                     alt: edge.node.altText ?? "",
//                     mediaContentType: "IMAGE",
//                 })) ?? [],
//         };
//     } catch (err) {
//         console.error("Failed to fetch product:", err);
//         return null;
//     }
// };

const fetchProductById = async (productId: string) => {
    const query = `
        query GetProductById($id: ID!) {
            product(id: $id) {
                id
                title
                descriptionHtml
                handle
                status
                images(first: 50) {
                    edges {
                        node {
                            id
                            url
                            altText
                        }
                    }
                }
            }
        }
    `;
    try {
        const res = await postData({
            url: endPoints.executeGraphql,
            payload: {
                query,
                variables: {
                    id: productId,
                },
            },
        });

        if (res?.status === 200 || res?.status === 201) {
            const product = res?.data?.data?.data?.product;
            if (!product) return null;

            return {
                id: product.id,
                title: product.title,
                handle: product.handle,
                status: product.status,
                descriptionHtml: product.descriptionHtml,
                images:
                    product.images?.edges?.map((edge: any) => ({
                        originalSource: edge.node.url,
                        alt: edge.node.altText ?? "img",
                        mediaContentType: "IMAGE",
                    })) ?? [],
            };
        }
        return null;
    } catch (error) {
        console.error("Failed to fetch product by ID", error);
        return null;
    }
};

const getBadgeTone = (status: string) => {
    switch (status) {
        case "DRAFT":
            return "attention";
        case "UNLISTED":
            return "info";
        case "ACTIVE":
            return "success";
        default:
            return undefined;
    }
};

const ProductIntent = ({
    bundleConfig,
    bundle_as_product,
    product_bundle_id,
    dispatchBundleConfig,
    setConfig,
    shakeSaveBar,
    globalDirty,
    setSavingBundle,
    product_title,
}: ProductIntentProps) => {
    const handleUpdateBundle = async (bundleConfig: any) => {
        try {
            shopify.saveBar.show("bundle-save-bar");
            setSavingBundle(true);

            if (!bundleConfig.form_id) return;
            const res = await postData({
                url: endPoints.updateBundle,
                payload: bundleConfig,
            });

            if (res.status === 200 || res.status === 201) {
                setConfig((ps: any) => ({
                    ...ps,
                    ...bundleConfig,
                }));
                dispatchBundleConfig({
                    type: ChangeEvents.initState,
                    init: {
                        ...bundleConfig,
                    },
                });
                shopify.toast.show("Bundle Product Updated", { duration: 1500 });
            } else {
                shopify.toast.show("Failed to save data. Please try again.", {
                    isError: true,
                    duration: 2000,
                });
            }
        } catch (error) {
            shopify.toast.show("Bundle Product Updation Failed", { isError: true, duration: 1500 });
        } finally {
            setSavingBundle(false);
            shopify.saveBar.hide("bundle-save-bar");
        }
    };

    const handleEditProduct = async () => {
        try {
            if (globalDirty) {
                shakeSaveBar();
                return;
            }
            // @ts-expect-error it exists
            const activity = await shopify.intents.invoke("edit:shopify/Product", {
                value: product_bundle_id,
            });

            const response = await activity.complete;

            if (response.code !== "ok") return;

            console._log("Intent response:", response.data);

            const productInfo = await fetchProductById(response.data.id);
            if (!productInfo) return;

            console._log("Product updated:", productInfo);

            const updatedConfig = {
                ...bundleConfig,
                bundle_as_product: {
                    ...bundle_as_product,
                    images: productInfo?.images,
                    product_title: productInfo?.title,
                    product_description: productInfo?.descriptionHtml,
                    product_status: productInfo?.status,
                },
                title: productInfo.title,
            };

            await handleUpdateBundle(updatedConfig);
        } catch (error) {
            console._log("Some error occured while updating bundle product intent.");
        }
    };

    return (
        <div className={styles.intentWrapper}>
            <div className={styles.intentHeader}>
                <div className={styles.intentHeaderLeft}>
                    <Text as="strong">Bundle Product</Text>
                    {bundle_as_product.product_status && (
                        <Badge size="small" tone={getBadgeTone(bundle_as_product.product_status)}>
                            {bundle_as_product.product_status
                                .toLowerCase()
                                .replace(/^\w/, (c: string) => c.toUpperCase())}
                        </Badge>
                    )}
                </div>

                {/* <Button variant="plain">Sync Product</Button> */}
            </div>

            <div className={styles.intentBody}>
                <div className={styles.intentBodyLeft}>
                    <Thumbnail
                        source={bundle_as_product?.images?.[0]?.originalSource ?? ProductIcon}
                        alt={""}
                        size="medium"
                    />
                    <Text as="h5">{product_title}</Text>
                </div>
                <div className={styles.intentEditButton} onClick={handleEditProduct}>
                    <Icon source={EditIcon} tone={"subdued"} accessibilityLabel="Edit" />
                </div>
            </div>
        </div>
    );
};

export default ProductIntent;
